import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { getTransactionLineChartData } from "../../../../../api";
import useMerchantStore from "../../../../stores/merchant";
import moment from "moment";
import Spinner from "../../../../components/Loaders/Spinner";
import SkeletonLoader from "../../../../components/skeleton-loader";

const SalesSplineChart = ({ type, startDate, endDate }) => {
  const activeApp = useMerchantStore((state) => state.activeApp);
  const [salesData, setSalesData] = useState([]);
  const [chargebackData, setChargebackData] = useState([]);
  const [dates, setDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!activeApp?.id) return;

      // Set default date range to last 12 months
      const start = startDate ? moment(startDate) : moment().startOf("month");
      const end = endDate ? moment(endDate) : moment();

      setIsLoading(true);
      try {
        const response = await getTransactionLineChartData({
          mid: activeApp?.power_app_submission?.mid,
          type: type,
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
        });

        if (response.status) {
          setDates(response.data.dates);
          setSalesData(response.data.transactions);
          setChargebackData(response.data.chargebacks);
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [activeApp?.id, type, startDate, endDate]);

  const options = {
    chart: {
      type: "area",
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    colors: ["#6366F1", "#F43F5E", "#FFB020"],
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 1,
      strokeWidth: 0,
      hover: {
        size: 6,
        sizeOffset: 3,
      },
    },
    xaxis: {
      categories: dates,
      tickAmount: 10,
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "12px",
          fontFamily: "Work Sans, sans-serif",
        },
        formatter: function (value) {
          // Format date ranges to show only the end date
          if (value && value.includes(' to ')) {
            const [startDate, endDate] = value.split(' to ');
            const end = moment(endDate, 'D MMM, YY').format('D MMM');
            return end;
          }
          return value;
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "12px",
          fontFamily: "Work Sans, sans-serif",
        },
        formatter: (value) => {
          if (type == "count") {
            return `${value}`;
          }
          return `$ ${Math.round(value / 1000)}K`;
        },
      },
    },
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: 20,
        bottom: 0,
        left: 20,
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "left",
      fontSize: "14px",
      fontFamily: "Work Sans, sans-serif",
      markers: {
        radius: 12,
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      followCursor: true,
      intersect: false,
      x: {
        format: "MMM dd, yyyy",
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const dateRange = w.globals.categoryLabels[dataPointIndex];
        const sales = series[0][dataPointIndex];
        const chargebacks = series[1][dataPointIndex];

        // Parse the date range and format to show only the end date
        let formattedDate = dateRange;
        if (dateRange && dateRange.includes(' to ')) {
          const [startDate, endDate] = dateRange.split(' to ');
          const end = moment(endDate, 'D MMM, YY').format('D MMM');
          formattedDate = end;
        }

        const chargebackPercentage =
          sales > 0 ? ((chargebacks / sales) * 100).toFixed(1) : 0;

        return `
          <div class="apexcharts-tooltip-box" style="font-family: Work Sans, sans-serif; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); padding: 8px 16px;">
            <div style="font-size: 14px; color: #64748B; margin-bottom: 12px;">${formattedDate}</div>
            <div style="margin-bottom: 8px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="background: #6366F1; width: 8px; height: 8px; border-radius: 50%;"></span>
                <span style="color: #1E293B;">Sales: ${type == "count" ? "" : "$"} ${sales?.toLocaleString() || 0}</span>
              </div>
            </div>
            <div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="background: #F43F5E; width: 8px; height: 8px; border-radius: 50%;"></span>
                <span style="color: #1E293B;">Chargebacks: ${chargebackPercentage}% ${type == "count" ? "" : "$"} ${chargebacks?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>
        `;
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [50, 100, 100],
      },
    },
  };

  const series = [
    {
      name: "Sales",
      data: salesData,
    },
    {
      name: "Chargebacks",
      data: chargebackData,
    },
  ];

  if (isLoading) {
    return <SkeletonLoader variant="rectangular" width="100%" height="364px" />;
  }

  return (
    <div>
      <Chart options={options} series={series} type="area" height={350} />
    </div>
  );
};

export default SalesSplineChart;
